// Domain: balance calculations

import type { SplitDetails } from "@/types/GroupTransaction";
import type { CurrencyBalances, IndividualCurrencyBalances, PairwiseDebt } from "@/types/Balance";
import { UserEntry } from "@/types/Group";

/** @see currencyDefaults.ts — FX defaults and supported profile currencies. */
export {
    SUPPORTED_APP_CURRENCIES,
    DEFAULT_DISPLAY_CURRENCY,
    DEFAULT_CONVERSION_RATES,
    DEFAULT_CONVERSION_RATES_MODE,
    FX_RATE_MIN,
    FX_RATE_MAX,
    autoConversionRatesForDisplay,
    buildReferenceValuesInArs,
    defaultConversionRatesForDisplay,
    isConversionRatesMode,
    isSupportedAppCurrency,
} from "shared/money/currencyDefaults";
export type { AppCurrencyCode, ConversionRatesMode, CurrencyReferenceValuesInArs } from "shared/money/currencyDefaults";
/** @see fx.ts */
export { convertBalanceToDisplayCurrency, mergeConversionRates, toDisplayAmount } from "shared/money/fx";

export type TransactionLike = {
    payer?: string;
    amount?: number;
    splitDetails?: SplitDetails;
    currency?: string;
};

function buildParticipantResolver(people: UserEntry[]) {
    const ids = new Set(people.map((person) => person.id).filter(Boolean));
    const displayNameCounts = new Map<string, number>();
    const normalizeKey = (value: string) => value.trim().toLowerCase();

    for (const person of people) {
        if (!person.displayName) continue;
        const displayNameKey = normalizeKey(person.displayName);
        displayNameCounts.set(displayNameKey, (displayNameCounts.get(displayNameKey) ?? 0) + 1);
    }

    const uniqueDisplayNameToId = new Map<string, string>();
    for (const person of people) {
        if (!person.displayName || !person.id) continue;
        const displayNameKey = normalizeKey(person.displayName);
        if (displayNameCounts.get(displayNameKey) === 1) {
            uniqueDisplayNameToId.set(displayNameKey, person.id);
        }
    }

    return (value?: string) => {
        if (!value) return value;
        if (ids.has(value)) return value;
        return uniqueDisplayNameToId.get(normalizeKey(value)) ?? value;
    };
}

export function normalizeTransactionParticipants<T extends TransactionLike>(
    txs: T[],
    people: UserEntry[]
): T[] {
    const resolveParticipant = buildParticipantResolver(people);

    return txs.map((tx) => {
        const normalizedSplitDetails: SplitDetails = {};
        for (const [rawPerson, rawAmount] of Object.entries(tx.splitDetails ?? {})) {
            const person = resolveParticipant(rawPerson) ?? rawPerson;
            normalizedSplitDetails[person] = (normalizedSplitDetails[person] ?? 0) + (Number(rawAmount) || 0);
        }

        return {
            ...tx,
            payer: resolveParticipant(tx.payer),
            splitDetails: tx.splitDetails ? normalizedSplitDetails : tx.splitDetails,
        };
    });
}

export function calcIndividualBalances(txs: TransactionLike[], user: string) {
    const balances: Record<string, number> = {};
    for (const tx of txs) {
        const userShare = tx?.splitDetails?.[user] ?? 0;
        if (tx?.payer === user) {
            for (const [person, amount] of Object.entries(tx?.splitDetails || {})) {
                if (person !== user) balances[person] = (balances[person] ?? 0) + (Number(amount) || 0);
            }
        } else if (tx?.payer) {
            balances[tx.payer] = (balances[tx.payer] ?? 0) - userShare;
        }
    }
    return balances;
}

export function calcUserBalance(txs: TransactionLike[], user: string) {
    let balance = 0;
    for (const tx of txs) {
        const userShare = tx?.splitDetails?.[user] ?? 0;
        balance += tx?.payer === user ? ((tx?.amount || 0) - userShare) : (-userShare);
    }
    return balance;
}

export function calcIndividualBalancesByCurrency(
    txs: TransactionLike[],
    user: string
): IndividualCurrencyBalances {
    const balances: IndividualCurrencyBalances = {};
    for (const tx of txs) {
        const currency = tx?.currency || 'USD';
        const userShare = tx?.splitDetails?.[user] ?? 0;
        if (tx?.payer === user) {
            for (const [person, amount] of Object.entries(tx?.splitDetails || {})) {
                if (person !== user) {
                    if (!balances[person]) balances[person] = {};
                    balances[person][currency] = (balances[person][currency] ?? 0) + (Number(amount) || 0);
                }
            }
        } else if (tx?.payer) {
            if (!balances[tx.payer]) balances[tx.payer] = {};
            balances[tx.payer][currency] = (balances[tx.payer][currency] ?? 0) - userShare;
        }
    }
    return balances;
}

/**
 * Aggregates liquidity pools + portfolio positions into balances by currency.
 * Used for total holdings display on profile.
 */
export function computeHoldingsByCurrency(
    pools: { currentBalance: number; currency: string }[],
    positions: { quantity: number; avgPrice: number; currentPrice?: number | null; currency?: string }[]
): CurrencyBalances {
    const byCurrency: CurrencyBalances = {};
    for (const p of pools) {
        const cur = p.currency || "USD";
        byCurrency[cur] = (byCurrency[cur] ?? 0) + (p.currentBalance ?? 0);
    }
    for (const pos of positions) {
        const cur = pos.currency ?? "USD";
        const price = pos.currentPrice ?? pos.avgPrice ?? 0;
        const value = (pos.quantity ?? 0) * price;
        byCurrency[cur] = (byCurrency[cur] ?? 0) + value;
    }
    return byCurrency;
}

export function calcUserBalanceByCurrency(txs: TransactionLike[], user: string): CurrencyBalances {
    const balances: CurrencyBalances = {};
    for (const tx of txs) {
        const currency = tx?.currency || 'ARS';
        const userShare = tx?.splitDetails?.[user] ?? 0;
        const contribution = tx?.payer === user ? ((tx?.amount || 0) - userShare) : (-userShare);
        balances[currency] = (balances[currency] ?? 0) + contribution;
    }
    return balances;
}

/**
 * Aggregates all pairwise debts from transactions (who owes whom, per currency).
 * Returns only debts with significant amount.
 */
export function calcPairwiseDebtsByCurrency(txs: TransactionLike[], people: UserEntry[]): PairwiseDebt[] {
    const normalizedTxs = normalizeTransactionParticipants(txs, people);

    // debtor|creditor|currency → aggregated amount
    const aggregated = new Map<string, number>();

    for (const tx of normalizedTxs) {
        if (!tx?.payer) continue;
        const currency = tx.currency || 'USD';

        for (const [personId, share] of Object.entries(tx.splitDetails ?? {})) {
            const amount = Number(share) || 0;
            if (personId === tx.payer || amount <= 0) continue;

            const key = `${personId}|${tx.payer}|${currency}`;
            aggregated.set(key, (aggregated.get(key) ?? 0) + amount);
        }
    }

    const result: PairwiseDebt[] = [];
    for (const [key, amount] of aggregated) {
        if (!isSignificantAmount(amount)) continue;
        const [debtor, creditor, currency] = key.split('|');
        result.push({ debtor, creditor, currency, amount });
    }
    return result;
}

/**
 * Simplifies pairwise debts so that the minimum number of transactions would settle everyone.
 * E.g. if A owes B 100 and B owes C 100, returns only A owes C 100.
 * Works per currency: computes net balance per person per currency, then greedily matches
 * debtors to creditors.
 */
export function simplifyDebts(debts: PairwiseDebt[]): PairwiseDebt[] {
    const netByPerson: Record<string, Record<string, number>> = {};
    for (const { debtor, creditor, currency, amount } of debts) {
        if (!netByPerson[debtor]) netByPerson[debtor] = {};
        if (!netByPerson[creditor]) netByPerson[creditor] = {};
        netByPerson[debtor][currency] = (netByPerson[debtor][currency] ?? 0) - amount;
        netByPerson[creditor][currency] = (netByPerson[creditor][currency] ?? 0) + amount;
    }
    const result: PairwiseDebt[] = [];
    const currencies = new Set<string>();
    for (const byCur of Object.values(netByPerson)) {
        for (const c of Object.keys(byCur)) currencies.add(c);
    }
    for (const currency of currencies) {
        const debtors: { person: string; amount: number }[] = [];
        const creditors: { person: string; amount: number }[] = [];
        for (const [person, byCur] of Object.entries(netByPerson)) {
            const amt = byCur[currency];
            if (!amt || !isSignificantAmount(amt)) continue;
            if (amt < 0) debtors.push({ person, amount: -amt });
            else creditors.push({ person, amount: amt });
        }
        let i = 0;
        let j = 0;
        while (i < debtors.length && j < creditors.length) {
            const d = debtors[i];
            const c = creditors[j];
            const transfer = Math.min(d.amount, c.amount);
            if (!isSignificantAmount(transfer)) {
                if (transfer === d.amount) i++;
                else j++;
                continue;
            }
            result.push({ debtor: d.person, creditor: c.person, currency, amount: transfer });
            d.amount -= transfer;
            c.amount -= transfer;
            if (!isSignificantAmount(d.amount)) i++;
            if (!isSignificantAmount(c.amount)) j++;
        }
    }
    return result;
}

export interface DerivedBalancesResult {
    individualBalances: Record<string, number>;
    userBalance: number;
    individualBalancesByCurrency: IndividualCurrencyBalances;
    userBalanceByCurrency: CurrencyBalances;
}

/**
 * Derives "your balance" and "individual balances with you" from a list of pairwise debts.
 * Only debts where the user is debtor or creditor are considered.
 * Used when simplify debt is on so both sections reflect the same simplified view.
 */
export function derivedBalancesFromPairwiseDebts(
    debts: PairwiseDebt[],
    username: string
): DerivedBalancesResult {
    const userBalanceByCurrency: CurrencyBalances = {};
    const individualBalancesByCurrency: IndividualCurrencyBalances = {};

    for (const { debtor, creditor, currency, amount } of debts) {
        const userIsCreditor = creditor === username;
        const userIsDebtor = debtor === username;
        if (!userIsDebtor && !userIsCreditor) continue;
        if (!isSignificantAmount(amount)) continue;

        if (userIsCreditor) {
            userBalanceByCurrency[currency] = (userBalanceByCurrency[currency] ?? 0) + amount;
            if (!individualBalancesByCurrency[debtor]) individualBalancesByCurrency[debtor] = {};
            individualBalancesByCurrency[debtor][currency] =
                (individualBalancesByCurrency[debtor][currency] ?? 0) + amount;
        }
        if (userIsDebtor) {
            userBalanceByCurrency[currency] = (userBalanceByCurrency[currency] ?? 0) - amount;
            if (!individualBalancesByCurrency[creditor]) individualBalancesByCurrency[creditor] = {};
            individualBalancesByCurrency[creditor][currency] =
                (individualBalancesByCurrency[creditor][currency] ?? 0) - amount;
        }
    }

    let userBalance = 0;
    for (const amt of Object.values(userBalanceByCurrency)) {
        userBalance += amt;
    }

    const individualBalances: Record<string, number> = {};
    for (const [person, byCur] of Object.entries(individualBalancesByCurrency)) {
        const sum = Object.values(byCur).reduce((a, b) => a + b, 0);
        if (isSignificantAmount(sum)) individualBalances[person] = sum;
    }

    return {
        individualBalances,
        userBalance,
        individualBalancesByCurrency,
        userBalanceByCurrency,
    };
}

export function getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = {
        'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'ARS': '$', 'BRL': 'R$',
        'CAD': 'C$', 'CNY': '¥', 'INR': '₹', 'MXN': '$', 'AUD': 'A$',
    };
    return symbols[currency] || currency + ' ';
}

export const MASKED_BALANCE_PLACEHOLDER = "••••••";

/** Maps app language (e.g. "es", "en") to locale for number/date formatting */
export function languageToLocale(lang: string): string {
    const base = lang.split("-")[0].toLowerCase();
    return base === "es" ? "es-AR" : "en-US";
}

export function formatCurrencyAmount(amount: number, currency: string, locale?: string): string {
    const abs = Math.abs(amount);
    const loc = locale ?? "es-AR";
    const formatted = abs.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${getCurrencySymbol(currency)}${formatted}`;
}

export function formatCurrencyAmountOrMasked(
    amount: number,
    currency: string,
    visible: boolean,
    locale?: string
): string {
    return visible ? formatCurrencyAmount(amount, currency, locale) : MASKED_BALANCE_PLACEHOLDER;
}

export function getBalanceColorClass(isPositive: boolean): string {
    return isPositive ? 'text-positive' : 'text-negative';
}

export function isSignificantAmount(amount: number, threshold: number = 0.01): boolean {
    return Math.abs(amount) >= threshold;
}
